/**
 * Accounting > Payroll Planner. This payroll (hours by person, four steps), the pay
 * calendar, the cash check, the season plan and the team. Built 2026-10-13 from the
 * prototype Mercedes made.
 *
 * The planner is one document on the server (payroll-planner.js, limited by name to Greg
 * and Mercedes because it lists everyone's pay). The drawing and arithmetic are in
 * public/payroll-planner-shell.html, shown here in a frame.
 *
 * Alongside the document the server sends `pto` (time off balances from the Employee Paid
 * Time Off reports under Payroll Records) and `tcp` (TimeClock Plus names with hire and
 * termination dates, so people who have left drop off). Neither is saved into the document.
 *
 * The frame cannot sign requests itself, so it posts messages here:
 *   planner-save      the whole document, after every change. Saved with the version this
 *                     page last read; a save made on a stale version is refused and the
 *                     frame tells the person to reload.
 *   planner-clock     hours for one pay period from the time clock.
 *   planner-baseline  last year's cash and orders for the comparison panel.
 *   planner-send      the "Send update to Greg" email. First with confirm:false for the
 *                     preview, then confirm:true to actually send.
 */
import React, { useEffect, useRef, useState } from 'react';
import { payrollPlanner } from '../services/api';
import { getCurrentUser } from '../services/auth';

// The shell is page content only. This gives it a document.
const wrap = (shell, data) => `<!doctype html><html data-theme="light"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>html{color-scheme:light}body{margin:0}[hidden]{display:none!important}</style>
</head><body>${shell.replace('/*DATA*/', () => JSON.stringify(data).replace(/</g, '\\u003c'))}</body></html>`;

// Today in Maryland, as YYYY-MM-DD.
const todayEastern = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());

export default function PayrollPlanner() {
  const [html, setHtml] = useState('');
  const [err, setErr] = useState('');
  const [height, setHeight] = useState(900);
  const frame = useRef(null);
  const version = useRef(null);
  // Saves go one at a time, each on the version the one before it returned.
  const saving = useRef(false);
  const waiting = useRef(null);
  const stopped = useRef(false);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [doc, res] = await Promise.all([payrollPlanner('get'), fetch(`${process.env.PUBLIC_URL || ''}/payroll-planner-shell.html`)]);
        if (!res.ok) throw new Error('Could not load the Payroll Planner page.');
        const shell = await res.text();
        if (!live) return;
        version.current = doc.version;
        const u = getCurrentUser() || {};
        setHtml(wrap(shell, {
          doc: doc.data, adp: doc.adp || [], pto: doc.pto || [], tcp: doc.tcp || [], me: u.staffName || u.name || u.username || '',
          today: todayEastern(), savedOn: doc.updated_on, savedBy: doc.updated_by,
        }));
      } catch (e) { if (live) setErr(e.message || 'Could not load the Payroll Planner.'); }
    })();
    return () => { live = false; };
  }, []);

  useEffect(() => {
    const post = (m) => { try { frame.current.contentWindow.postMessage(m, '*'); } catch (_) { /* frame gone */ } };

    const drain = async () => {
      if (saving.current || stopped.current) return;
      saving.current = true;
      while (waiting.current && !stopped.current) {
        const data = waiting.current; waiting.current = null;
        try {
          const r = await payrollPlanner('save', { data, version: version.current });
          version.current = r.version;
          if (!waiting.current) post({ type: 'planner-save-done', ok: true, savedOn: r.updated_on, by: r.updated_by });
        } catch (e) {
          // A refused save must not be followed by another on the same stale version.
          const conflict = e.status === 409;
          if (conflict) stopped.current = true;
          post({ type: 'planner-save-done', ok: false, conflict, error: conflict ? e.message : `${e.message || 'Could not save.'} Your last change is not saved. Check the connection and make it again.` });
        }
      }
      saving.current = false;
    };

    const onMsg = async (e) => {
      if (!frame.current || e.source !== frame.current.contentWindow) return;
      const m = e.data || {};
      if (m.type === 'planner-save' && m.data) { waiting.current = m.data; drain(); return; }
      if (m.type === 'planner-clock') {
        try {
          const r = await payrollPlanner('clock', { start: m.start, stop: m.stop });
          post({ type: 'planner-clock-done', ok: true, run: m.run, start: r.start, stop: r.stop, people: r.people });
        } catch (ex) { post({ type: 'planner-clock-done', ok: false, run: m.run, error: ex.message || 'The time clock did not answer.' }); }
      }
      if (m.type === 'planner-baseline') {
        try {
          const r = await payrollPlanner('baseline', { run: m.run });
          post({ type: 'planner-baseline-done', ok: true, run: m.run, data: r });
        } catch (ex) { post({ type: 'planner-baseline-done', ok: false, run: m.run, error: ex.message || 'Could not load the comparison.' }); }
      }
      if (m.type === 'planner-send') {
        try {
          const r = await payrollPlanner('send-update', { subject: m.subject, html: m.html, confirm: !!m.confirm });
          post({ type: 'planner-send-done', ok: true, run: m.run, preview: !!r.preview, sent: !!r.sent, subject: r.subject, html: r.html, sentAt: r.sentAt, by: r.by });
        } catch (ex) { post({ type: 'planner-send-done', ok: false, run: m.run, error: ex.message || 'The update email did not send.' }); }
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // Size the frame to its content so the page scrolls as one.
  const onLoad = () => {
    const d = frame.current && frame.current.contentDocument;
    if (!d) return;
    const fit = () => setHeight(Math.max(600, d.documentElement.scrollHeight));
    fit();
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(fit).observe(d.body);
    d.addEventListener('click', () => setTimeout(fit, 0));
  };

  if (err) return <div style={{ padding: 24 }}><div style={{ background: '#fdeaea', color: '#b42318', padding: '10px 14px', borderRadius: 6, maxWidth: 640 }}>{err}</div></div>;
  if (!html) return <div style={{ padding: 24 }}>Loading the Payroll Planner...</div>;
  return (
    <iframe ref={frame} title="Payroll Planner" srcDoc={html} onLoad={onLoad}
      style={{ width: '100%', height, border: 0, display: 'block' }} />
  );
}
